import isPropValid from '@emotion/is-prop-valid';
import { Theme } from 'providers/Theme';
import React from 'react';
import styled, { CSSProp } from 'styled-components';
import {
  ColorProps,
  FlexboxProps,
  LayoutProps,
  SpaceProps,
  TypographyProps,
  color,
  compose,
  flexbox,
  layout,
  space,
  system,
  typography,
} from 'styled-system';

// Define a type for the custom `sx` and `__css` props and the variant props
export interface BoxProps
  extends SpaceProps,
    LayoutProps,
    TypographyProps,
    ColorProps,
    FlexboxProps {
  sx?: object;
  __css?: object;
  variant?: string;
  tx?: string;
  as?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
  css?: CSSProp;
  theme?: Theme;
}

// Extend the styled-system's `css` function to support custom props
const css = (args: any) => (props: any) => system(args)(props);

const sx = (props: BoxProps) => css(props.sx);
const base = (props: BoxProps) => css(props.__css);

// Implement a simple `get` function for accessing theme values
const get = (object: any, path: string, fallback?: any): any => {
  return (
    path.split('.').reduce((accumulator, key) => {
      return accumulator && accumulator[key] !== undefined
        ? accumulator[key]
        : undefined;
    }, object) || fallback
  );
};

const variant = ({
  theme,
  variant: variantProp,
  tx = 'variants',
}: BoxProps) => {
  const path = `${tx}.${variantProp}`;
  const variantStyles = get(theme, path || '', get(theme, variantProp || ''));
  return css(variantStyles);
};

export const Box = styled.div<BoxProps>(
  {
    boxSizing: 'border-box',
    margin: 0,
    minWidth: 0,
  },
  base,
  variant,
  sx,
  compose(space, layout, typography, color, flexbox),
);

export const Flex = styled(Box).withConfig({
  shouldForwardProp: (prop) => {
    return isPropValid(prop);
  },
})<BoxProps>({
  display: 'flex',
});

export const Center = styled(Flex)``;

Center.defaultProps = {
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  flex: 1,
};
